import React, { useState, useEffect } from "react";
import { FaEdit, FaTrash, FaCheck, FaTimes } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAcademicEventTypes,
  createAcademicEventTypes,
  editAcademicEventTypes,
  deleteAcademicEventTypes,
  setNotification,
} from "../../store/academicCalendarFormSlice.js";

const EventTypeForm = ({ onClose }) => {
  const dispatch = useDispatch();
  const { allEventTypes } = useSelector(
    (state) => state.academicCalendar || []
  );
  const [newEventType, setNewEventType] = useState("");
  const [newEventTypeAdded, setNewEventTypeAdded] = useState(0);
  const [newEventDescreption, setNewEventDescreption] = useState("");
  const [selectedEditingEventId, setSelectedEditingEventId] = useState(null);
  const [isEditing, setIsEditing] = useState(false); // New state to track edit mode

  useEffect(() => {
    dispatch(fetchAcademicEventTypes());
  }, [dispatch]);

  useEffect(() => {
    if (newEventTypeAdded > 0) dispatch(fetchAcademicEventTypes());
  }, [dispatch, newEventTypeAdded]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isEditing) {
      // Handle edit submission
      const eventType = newEventType.trim();
      const eventDescription = newEventDescreption.trim();

      if (!eventType) {
        dispatch(setNotification("Event type cannot be empty."));
        return;
      }

      if (
        allEventTypes?.some(
          (et) =>
            et.eventName.toLowerCase() === eventType.toLowerCase() &&
            et.eventTypeID !== selectedEditingEventId
        )
      ) {
        dispatch(setNotification("Event type must be unique."));
        return;
      }

      try {
        console.log("selectedEditingEventId", selectedEditingEventId);
        console.log("eventType", eventType);
        console.log("eventDescription", eventDescription);
        await dispatch(
          editAcademicEventTypes({
            eventTypeId: selectedEditingEventId,
            eventTypeData: {
              eventName: eventType,
              eventDescription: eventDescription,
            },
          })
        ).unwrap();
        setNewEventTypeAdded(newEventTypeAdded + 1);
        resetForm();
        dispatch(setNotification("Event type updated successfully!"));
      } catch (error) {
        dispatch(
          setNotification(error.message || "Failed to update event type")
        );
      }
    } else {
      // Handle create submission
      const eventType = newEventType.trim();
      const eventDescription = newEventDescreption.trim();

      if (!eventType) {
        dispatch(setNotification("Event type cannot be empty."));
        return;
      }

      if (
        allEventTypes?.some(
          (et) => et.eventName.toLowerCase() === eventType.toLowerCase()
        )
      ) {
        dispatch(setNotification("Event type must be unique."));
        return;
      }

      try {
        await dispatch(
          createAcademicEventTypes({
            eventName: eventType,
            eventDescription: eventDescription,
          })
        ).unwrap();
        setNewEventTypeAdded(newEventTypeAdded + 1);
        resetForm();
        dispatch(setNotification("Event type added successfully!"));
      } catch (error) {
        dispatch(setNotification(error.message || "Failed to add event type"));
      }
    }
  };

  const resetForm = () => {
    setNewEventType("");
    setNewEventDescreption("");
    setSelectedEditingEventId(null);
    setIsEditing(false);
  };

  const handleEditStart = (eventId, eventName, eventDescription) => {
    setNewEventType(eventName);
    setNewEventDescreption(eventDescription);
    setSelectedEditingEventId(eventId);
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    resetForm();
  };

  const handleDelete = async (eventTypeID) => {
    if (!window.confirm("Are you sure you want to delete this event type?")) {
      return;
    }
    try {
      await dispatch(deleteAcademicEventTypes(eventTypeID)).unwrap();
      setNewEventTypeAdded(newEventTypeAdded + 1);
      dispatch(setNotification("Event type deleted successfully!"));
    } catch (error) {
      dispatch(setNotification(error.message || "Failed to delete event type"));
    }
  };

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-labelledby="manage-event-title"
    >
      <div className="modal-content">
        <h2 id="manage-event-title">Manage Event Types</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="newEventType">
              {isEditing ? "Edit Event Type" : "New Event Type"}
            </label>
            <input
              type="text"
              id="newEventType"
              value={newEventType}
              onChange={(e) => setNewEventType(e.target.value)}
              placeholder={
                isEditing ? "Edit event type" : "Enter new event type"
              }
              required
            />
            <label htmlFor="newEventDescreption">
              {isEditing ? "Edit Event Description" : "New Event Description"}
            </label>
            <input
              type="text"
              id="newEventDescreption"
              value={newEventDescreption}
              onChange={(e) => setNewEventDescreption(e.target.value)}
              placeholder={
                isEditing
                  ? "Edit event description"
                  : "Enter new event description"
              }
              required
            />
          </div>
          <div className="form-actions">
            <button type="submit" className="submit-btn">
              {isEditing ? "Update Event Type" : "Add Event Type"}
            </button>
            {isEditing && (
              <button
                type="button"
                className="cancel-btn"
                onClick={handleEditCancel}
              >
                Cancel Edit
              </button>
            )}
            <button type="button" className="cancel-btn" onClick={onClose}>
              Close
            </button>
          </div>
        </form>
        <div className="event-types-list">
          <h3>Existing Event Types</h3>
          <ul>
            {allEventTypes?.map((et) => (
              <li key={et.eventTypeID} className="event-type-item">
                <div className="event-type-info">
                  <div className="event-name-wrapper">
                    <span className="event-name">
                      <span
                        className="label"
                        style={{
                          color: "#28a745",
                          fontWeight: "bold",
                          marginRight: "8px",
                        }}
                      >
                        Event Name
                      </span>
                      <span className="value">{et.eventName}</span>
                    </span>

                    {et.eventDescription && (
                      <div className="event-description-tooltip">
                        {et.eventDescription}
                      </div>
                    )}
                  </div>
                </div>
                <div className="event-type-actions">
                  <button
                    className="action-btn edit-btn"
                    onClick={() =>
                      handleEditStart(
                        et.eventTypeID,
                        et.eventName,
                        et.eventDescription
                      )
                    }
                    title={`Edit ${et.eventName}`}
                    aria-label={`Edit ${et.eventName} event type`}
                  >
                    <FaEdit />
                  </button>
                  <button
                    className="action-btn delete-btn"
                    onClick={() => handleDelete(et.eventTypeID)}
                    title={`Delete ${et.eventName}`}
                    aria-label={`Delete ${et.eventName} event type`}
                  >
                    <FaTrash />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EventTypeForm;
